import { BookmarkPlus, Plus, Trash2 } from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { ConfirmModal } from "@/components/ConfirmModal";
import { Modal } from "@/components/Modal";
import { estimateMeal } from "@/lib/estimateMeal";
import {
  type AmountUnit,
  type ChatMessage,
  type Ingredient,
  MEAL_METRICS,
  type Meal,
  type MealCondition,
  type MealMetric,
  type Recipe,
  type RecipeComponent,
} from "@/lib/schema";
import {
  AMOUNT_UNIT_LABELS,
  componentAmount,
  componentEatenGrams,
  componentFinishedGrams,
  componentPortions,
  componentRawGrams,
  componentScale,
  mealScale,
  recipeFinishedGrams,
  recipePortions,
  sumGrams,
  unitsFor,
} from "./amount";
import { MetricCellDensity } from "./MetricCellDensity";
import { formatAmount, METRIC_LABELS, metricUnit } from "./metrics";
import { RecipeEditorModal } from "./RecipeEditorModal";
import { sumMetrics } from "./scoring";

interface Props {
  open: boolean;
  initial: Meal | null;
  conditions: MealCondition[];
  // Current Matpresiseringer texts (live settings, docs/adr/0004) —
  // sent with every estimate request.
  foodClarifications?: string[];
  // Live per-user recipe list (docs/adr/0005) for the import picker.
  recipes?: Recipe[];
  onSaveRecipe: (recipe: Recipe) => void;
  onSave: (meal: Meal) => void;
  onClose: () => void;
}

export function MealFormModal({
  open,
  initial,
  conditions,
  foodClarifications,
  recipes,
  onSaveRecipe,
  onSave,
  onClose,
}: Props) {
  if (!open) return null;
  return (
    <ModalBody
      key={initial?.id ?? "new"}
      initial={initial}
      conditions={conditions}
      foodClarifications={foodClarifications}
      recipes={recipes ?? []}
      onSaveRecipe={onSaveRecipe}
      onSave={onSave}
      onClose={onClose}
    />
  );
}

type MetricInputs = Record<MealMetric, string>;

function metricInputsFrom(
  source: Record<MealMetric, number> | null,
): MetricInputs {
  const inputs = {} as MetricInputs;
  for (const metric of MEAL_METRICS) {
    inputs[metric] = source
      ? String(Math.round(source[metric] * 10) / 10)
      : "0";
  }
  return inputs;
}

function parseMetric(value: string): number {
  const trimmed = value.trim();
  if (trimmed.length === 0) return Number.NaN;
  const parsed = Number(trimmed.replace(",", "."));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : Number.NaN;
}

// An eaten amount must be strictly positive, whatever unit it is in.
function parseAmountValue(value: string): number {
  const parsed = parseMetric(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : Number.NaN;
}

// Gram figures read naturally at one decimal; a fraction of a recipe needs
// more, so switching units round-trips without visible drift (85 g of a
// 424 g batch is 0.2005 oppskrift, which converts back to 85 g).
function formatAmountValue(value: number, unit: AmountUnit): string {
  if (!Number.isFinite(value)) return "";
  const factor = unit === "grams" ? 10 : 10000;
  return String(Math.round(value * factor) / factor);
}

function roundMetrics(
  totals: Record<MealMetric, number>,
): Record<MealMetric, number> {
  const rounded = {} as Record<MealMetric, number>;
  for (const metric of MEAL_METRICS) {
    rounded[metric] = Math.round(totals[metric] * 10) / 10;
  }
  return rounded;
}

function scaleMetrics(
  amounts: Record<MealMetric, number>,
  portion: number,
): Record<MealMetric, number> {
  const scaled = {} as Record<MealMetric, number>;
  for (const metric of MEAL_METRICS) scaled[metric] = amounts[metric] * portion;
  return scaled;
}

// Components saved before units existed carry a bare `portion`; editing one
// upgrades it in place, which is lossless — a bare portion always meant a
// whole-recipe multiplier.
function normalizeComponent(component: RecipeComponent): RecipeComponent {
  const { portion: _legacy, ...rest } = component;
  return {
    ...rest,
    amount: componentAmount(component),
    finishedGrams: componentFinishedGrams(component),
    portions: componentPortions(component),
  };
}

// Grams edits scale the row's metrics from its estimated baseline, so
// repeated edits never compound. A 0 g baseline has no scale factor — its
// metrics stay as the estimate delivered them.
function scaleFromBaseline(baseline: Ingredient, grams: number): Ingredient {
  if (baseline.grams <= 0) return { ...baseline, grams };
  const factor = grams / baseline.grams;
  const scaled = { ...baseline, grams };
  for (const metric of MEAL_METRICS) scaled[metric] = baseline[metric] * factor;
  return scaled;
}

function ModalBody({
  initial,
  conditions,
  foodClarifications,
  recipes,
  onSaveRecipe,
  onSave,
  onClose,
}: {
  initial: Meal | null;
  conditions: MealCondition[];
  foodClarifications?: string[];
  recipes: Recipe[];
  onSaveRecipe: (recipe: Recipe) => void;
  onSave: (meal: Meal) => void;
  onClose: () => void;
}) {
  const initialIsEstimated =
    (initial?.ingredients?.length ?? 0) > 0 ||
    (initial?.recipeComponents?.length ?? 0) > 0;
  const [mode, setMode] = useState<"chat" | "manual">(
    initial && !initialIsEstimated ? "manual" : "chat",
  );
  const [name, setName] = useState(initial?.name ?? "");
  const [lastEstimateName, setLastEstimateName] = useState(
    initialIsEstimated ? (initial?.name ?? "") : "",
  );
  const [ingredients, setIngredients] = useState<Ingredient[]>(
    initial?.ingredients ?? [],
  );
  const [baselines, setBaselines] = useState<Ingredient[]>(
    initial?.ingredients ?? [],
  );
  const [gramsInputs, setGramsInputs] = useState<string[]>(() =>
    (initial?.ingredients ?? []).map((ing) => formatAmount(ing.grams)),
  );
  // The chat component's fraction: applied value + raw input. It has no
  // finished weight or portion count of its own, so it stays a bare
  // multiplier. The applied value only moves on a parseable input; an
  // unparseable input gates Save.
  const [chatScale, setChatScale] = useState(initial ? mealScale(initial) : 1);
  const [chatScaleInput, setChatScaleInput] = useState(
    formatAmount(initial ? mealScale(initial) : 1),
  );
  const [recipeComponents, setRecipeComponents] = useState<RecipeComponent[]>(
    () => (initial?.recipeComponents ?? []).map(normalizeComponent),
  );
  // Raw amount inputs keyed by component id; the unit lives on the component
  // itself, since a select is always parseable.
  const [recipeAmountInputs, setRecipeAmountInputs] = useState<
    Record<string, string>
  >(() =>
    Object.fromEntries(
      (initial?.recipeComponents ?? []).map((c) => {
        const amount = componentAmount(c);
        return [c.id, formatAmountValue(amount.value, amount.unit)];
      }),
    ),
  );
  const [importOpen, setImportOpen] = useState(false);
  const [saveRecipeOpen, setSaveRecipeOpen] = useState(false);
  const [transcript, setTranscript] = useState<ChatMessage[]>(
    initial?.transcript ?? [],
  );
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [confirmConvert, setConfirmConvert] = useState(false);
  const [metricInputs, setMetricInputs] = useState<MetricInputs>(() =>
    metricInputsFrom(initial && !initialIsEstimated ? initial : null),
  );

  // Keep the newest chat line visible — the log is a fixed-height scroller.
  const chatLogRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = chatLogRef.current;
    if (el && transcript.length > 0) el.scrollTop = el.scrollHeight;
  }, [transcript]);

  const hasComponents = ingredients.length > 0 || recipeComponents.length > 0;
  const chatSubtotal = sumMetrics(ingredients);
  const chatGrams = sumGrams(ingredients);
  // Grand totals = Σ scaled component subtotals — the values that save to
  // the meal and that scoring reads.
  const totals = scaleMetrics(
    chatSubtotal,
    ingredients.length > 0 ? chatScale : 0,
  );
  // Raw grams is the sum of the ingredient rows as scaled; eaten grams is
  // what a kitchen scale would say. They diverge for a recipe that gained or
  // lost weight while it was made; the chat component has no such fact, so
  // its two figures are identical.
  let totalRawGrams = ingredients.length > 0 ? chatGrams * chatScale : 0;
  let totalEatenGrams = totalRawGrams;
  for (const component of recipeComponents) {
    const subtotal = scaleMetrics(
      sumMetrics(component.ingredients),
      componentScale(component),
    );
    for (const metric of MEAL_METRICS) totals[metric] += subtotal[metric];
    totalRawGrams += componentRawGrams(component);
    totalEatenGrams += componentEatenGrams(component);
  }

  const amountsValid =
    (ingredients.length === 0 ||
      Number.isFinite(parseAmountValue(chatScaleInput))) &&
    recipeComponents.every((c) =>
      Number.isFinite(parseAmountValue(recipeAmountInputs[c.id] ?? "")),
    );

  const parsedMetrics = {} as Record<MealMetric, number>;
  for (const metric of MEAL_METRICS) {
    parsedMetrics[metric] = parseMetric(metricInputs[metric]);
  }
  const canSave =
    name.trim().length > 0 &&
    (mode === "chat"
      ? hasComponents && amountsValid
      : MEAL_METRICS.every((m) => Number.isFinite(parsedMetrics[m])));

  const send = async () => {
    const text = draft.trim();
    if (text.length === 0 || sending) return;
    const nextTranscript: ChatMessage[] = [
      ...transcript,
      { role: "user", text },
    ];
    setSending(true);
    setChatError(null);
    try {
      const estimate = await estimateMeal({
        transcript: nextTranscript,
        previousEstimate:
          ingredients.length > 0
            ? { name: name.trim(), ingredients }
            : undefined,
        foodClarifications:
          foodClarifications && foodClarifications.length > 0
            ? foodClarifications
            : undefined,
        recipeComponents:
          recipeComponents.length > 0
            ? recipeComponents.map((component) => ({
                name: component.name,
                scale: componentScale(component),
                ingredients: component.ingredients,
              }))
            : undefined,
      });
      setIngredients(estimate.ingredients);
      setBaselines(estimate.ingredients);
      setGramsInputs(
        estimate.ingredients.map((ing) => formatAmount(ing.grams)),
      );
      setTranscript([
        ...nextTranscript,
        { role: "assistant", text: estimate.comment },
      ]);
      // A user-edited name is not overwritten by later corrections.
      setName((prev) =>
        prev.trim().length === 0 || prev === lastEstimateName
          ? estimate.name
          : prev,
      );
      setLastEstimateName(estimate.name);
      setDraft("");
    } catch (err) {
      setChatError(
        err instanceof Error ? err.message : "Estimering feilet. Prøv igjen.",
      );
    } finally {
      setSending(false);
    }
  };

  const handleManualClick = () => {
    if (hasComponents) {
      setConfirmConvert(true);
      return;
    }
    setMode("manual");
  };

  const doConvert = () => {
    setMetricInputs(metricInputsFrom(totals));
    setIngredients([]);
    setBaselines([]);
    setGramsInputs([]);
    setChatScale(1);
    setChatScaleInput("1");
    setRecipeComponents([]);
    setRecipeAmountInputs({});
    setTranscript([]);
    setConfirmConvert(false);
    setMode("manual");
  };

  // Table edits are recorded into the transcript as event lines, so the
  // chat history explains the table and the AI never resurrects a deleted
  // ingredient (issue #43). Recipe imports and removals are evented for
  // the same reason; portion changes are not — the AI works at reference
  // scale and never needs them.
  const appendEvent = (text: string) => {
    setTranscript((prev) => [...prev, { role: "user", text, event: true }]);
  };

  const changeGrams = (index: number, value: string) => {
    setGramsInputs((prev) => prev.map((g, i) => (i === index ? value : g)));
    const grams = parseMetric(value);
    if (!Number.isFinite(grams)) return;
    setIngredients((prev) =>
      prev.map((ing, i) =>
        i === index ? scaleFromBaseline(baselines[index], grams) : ing,
      ),
    );
  };

  // One event per settled grams edit: capture the applied value on focus,
  // record on blur only if it actually changed.
  const gramsAtFocus = useRef<number | null>(null);
  const focusGrams = (index: number) => {
    gramsAtFocus.current = ingredients[index]?.grams ?? null;
  };
  const blurGrams = (index: number) => {
    const applied = ingredients[index];
    if (applied && gramsAtFocus.current !== null) {
      if (applied.grams !== gramsAtFocus.current) {
        appendEvent(
          `Endret ${applied.name} til ${formatAmount(applied.grams)} g.`,
        );
      }
    }
    gramsAtFocus.current = null;
  };

  const removeIngredient = (index: number) => {
    const removed = ingredients[index];
    setIngredients((prev) => prev.filter((_, i) => i !== index));
    setBaselines((prev) => prev.filter((_, i) => i !== index));
    setGramsInputs((prev) => prev.filter((_, i) => i !== index));
    if (removed) appendEvent(`Fjernet ${removed.name}.`);
  };

  const changeChatScale = (value: string) => {
    setChatScaleInput(value);
    const scale = parseAmountValue(value);
    if (Number.isFinite(scale)) setChatScale(scale);
  };

  const changeRecipeAmountValue = (id: string, value: string) => {
    setRecipeAmountInputs((prev) => ({ ...prev, [id]: value }));
    const parsed = parseAmountValue(value);
    if (!Number.isFinite(parsed)) return;
    setRecipeComponents((prev) =>
      prev.map((c) =>
        c.id === id
          ? { ...c, amount: { unit: componentAmount(c).unit, value: parsed } }
          : c,
      ),
    );
  };

  // Switching units converts the value rather than re-interpreting it, so
  // the displayed amount is always a true statement about what is applied —
  // 1 oppskrift of a 424 g batch becomes 424 gram, not 1 gram.
  const changeRecipeAmountUnit = (id: string, unit: AmountUnit) => {
    const component = recipeComponents.find((c) => c.id === id);
    if (!component) return;
    const scale = componentScale(component);
    const finished = componentFinishedGrams(component);
    const portions = componentPortions(component);
    const value =
      unit === "grams"
        ? scale * finished
        : unit === "portions"
          ? scale * portions
          : scale;
    setRecipeComponents((prev) =>
      prev.map((c) => (c.id === id ? { ...c, amount: { unit, value } } : c)),
    );
    setRecipeAmountInputs((prev) => ({
      ...prev,
      [id]: formatAmountValue(value, unit),
    }));
  };

  const importRecipe = (recipe: Recipe) => {
    const component: RecipeComponent = {
      id: crypto.randomUUID(),
      name: recipe.name,
      recipeId: recipe.id,
      amount: { unit: "recipe", value: 1 },
      // Copy on import (docs/adr/0005, docs/adr/0006) — later recipe edits
      // never reach this meal, including edits to these two numbers.
      finishedGrams: recipeFinishedGrams(recipe),
      portions: recipePortions(recipe),
      ingredients: recipe.ingredients.map((ing) => ({ ...ing })),
    };
    setRecipeComponents((prev) => [...prev, component]);
    setRecipeAmountInputs((prev) => ({ ...prev, [component.id]: "1" }));
    setImportOpen(false);
    // A still-nameless meal takes the recipe's name — same courtesy the
    // first estimate extends; a typed name is never overwritten.
    setName((prev) => (prev.trim().length === 0 ? recipe.name : prev));
    appendEvent(`La til oppskrift: ${recipe.name}.`);
  };

  const removeRecipeComponent = (id: string) => {
    const removed = recipeComponents.find((c) => c.id === id);
    setRecipeComponents((prev) => prev.filter((c) => c.id !== id));
    setRecipeAmountInputs((prev) => {
      const { [id]: _dropped, ...rest } = prev;
      return rest;
    });
    if (removed) appendEvent(`Fjernet oppskrift: ${removed.name}.`);
  };

  // «Lagre som oppskrift» hands the chat component to the recipe editor,
  // where the finished weight and portion count get set (docs/adr/0006).
  // The eaten fraction is a per-day fact and never enters the recipe.
  const saveRecipeFromEditor = (recipe: Recipe) => {
    onSaveRecipe(recipe);
    setSaveRecipeOpen(false);
  };

  const handleSave = () => {
    if (!canSave) return;
    if (mode === "chat") {
      onSave({
        id: initial?.id ?? crypto.randomUUID(),
        name: name.trim(),
        ...roundMetrics(totals),
        ...(ingredients.length > 0 ? { ingredients } : {}),
        ...(ingredients.length > 0 && chatScale !== 1
          ? { scale: chatScale }
          : {}),
        ...(recipeComponents.length > 0 ? { recipeComponents } : {}),
        ...(transcript.length > 0 ? { transcript } : {}),
      });
      return;
    }
    onSave({
      id: initial?.id ?? crypto.randomUUID(),
      name: name.trim(),
      ...parsedMetrics,
    });
  };

  const metricColumns = MEAL_METRICS.length + 2; // name + grams + metrics

  // The grams column sums the ingredient rows above it, so it stays the raw
  // figure; the measured weight rides along in parentheses whenever the dish
  // gained or lost weight while it was made.
  const gramsCell = (raw: number, eaten: number) =>
    Math.round(raw * 10) !== Math.round(eaten * 10) ? (
      <>
        {formatAmount(raw)}{" "}
        <span className="hint">(ferdig {formatAmount(eaten)} g)</span>
      </>
    ) : (
      formatAmount(raw)
    );

  const subtotalRow = (
    testid: string,
    amountCell: React.ReactNode,
    gramsContent: React.ReactNode,
    amounts: Record<MealMetric, number>,
    densityTestidFor: (conditionId: string) => string,
  ) => (
    <tr className="row-subtotal" data-testid={testid}>
      <td className="td-label portion-cell">{amountCell}</td>
      <td data-testid={`${testid}-grams`}>{gramsContent}</td>
      {MEAL_METRICS.map((metric) => (
        <td key={metric} data-testid={`${testid}-${metric}`}>
          {formatAmount(amounts[metric])}
          <MetricCellDensity
            conditions={conditions}
            metric={metric}
            amounts={amounts}
            testidFor={densityTestidFor}
          />
        </td>
      ))}
      <td />
    </tr>
  );

  return (
    <Modal open onClose={onClose} data-testid="meal-form-modal" size="xl">
      <h2>{initial ? "Rediger måltid" : "Legg til måltid"}</h2>

      <div className="meal-form-name">
        <label htmlFor="meal-name">Navn</label>
        <input
          id="meal-name"
          className="input"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="meal-name-input"
        />
      </div>

      {mode === "chat" ? (
        <>
          {recipes.length > 0 ? (
            <div className="recipe-import">
              <button
                type="button"
                className="btn ghost icon-btn"
                onClick={() => setImportOpen((prev) => !prev)}
                data-testid="add-recipe-button"
              >
                <Plus size={14} />
                Legg til oppskrift
              </button>
              {importOpen ? (
                <div className="recipe-import-menu">
                  {recipes.map((recipe) => (
                    <button
                      key={recipe.id}
                      type="button"
                      className="btn ghost recipe-option"
                      onClick={() => importRecipe(recipe)}
                      data-testid={`recipe-option-${recipe.id}`}
                    >
                      {recipe.name}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          {hasComponents ? (
            <div className="week-matrix meal-table">
              <table data-testid="meal-ingredients-table">
                <thead>
                  <tr>
                    <th className="th-group">Ingrediens</th>
                    <th>g</th>
                    {MEAL_METRICS.map((metric) => (
                      <th key={metric}>
                        {metric === "kcal" ? "kcal" : METRIC_LABELS[metric]}
                      </th>
                    ))}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {recipeComponents.map((component) => (
                    <Fragment key={component.id}>
                      <tr
                        className="component-head"
                        data-testid={`recipe-component-${component.id}`}
                      >
                        <td className="td-label" colSpan={metricColumns}>
                          {component.name}{" "}
                          <span className="component-kind">(oppskrift)</span>
                        </td>
                        <td className="td-actions">
                          <button
                            type="button"
                            className="icon-btn-delete"
                            onClick={() => removeRecipeComponent(component.id)}
                            aria-label={`Fjern oppskrift ${component.name}`}
                            data-testid={`delete-recipe-component-button-${component.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                      {component.ingredients.map((ing, index) => (
                        <tr
                          // biome-ignore lint/suspicious/noArrayIndexKey: read-only copied rows, never reordered.
                          key={index}
                          data-testid={`recipe-ingredient-row-${component.id}-${index}`}
                        >
                          <td className="td-label">{ing.name}</td>
                          <td>{formatAmount(ing.grams)}</td>
                          {MEAL_METRICS.map((metric) => (
                            <td key={metric}>
                              {formatAmount(ing[metric])}
                              <MetricCellDensity
                                conditions={conditions}
                                metric={metric}
                                amounts={ing}
                                testidFor={(conditionId) =>
                                  `recipe-ingredient-density-${component.id}-${index}-${conditionId}`
                                }
                              />
                            </td>
                          ))}
                          <td />
                        </tr>
                      ))}
                      {subtotalRow(
                        `recipe-subtotal-row-${component.id}`,
                        <>
                          <input
                            type="text"
                            inputMode="decimal"
                            className="portion-input"
                            value={recipeAmountInputs[component.id] ?? ""}
                            onChange={(e) =>
                              changeRecipeAmountValue(
                                component.id,
                                e.target.value,
                              )
                            }
                            aria-label={`Spist av ${component.name}`}
                            data-testid={`recipe-amount-input-${component.id}`}
                          />
                          <select
                            className="amount-unit-select"
                            value={componentAmount(component).unit}
                            onChange={(e) =>
                              changeRecipeAmountUnit(
                                component.id,
                                e.target.value as AmountUnit,
                              )
                            }
                            aria-label={`Enhet for ${component.name}`}
                            data-testid={`recipe-amount-unit-${component.id}`}
                          >
                            {unitsFor(componentPortions(component)).map(
                              (unit) => (
                                <option key={unit} value={unit}>
                                  {AMOUNT_UNIT_LABELS[unit]}
                                </option>
                              ),
                            )}
                          </select>
                          <span className="portion-label">Spist</span>
                        </>,
                        gramsCell(
                          componentRawGrams(component),
                          componentEatenGrams(component),
                        ),
                        scaleMetrics(
                          sumMetrics(component.ingredients),
                          componentScale(component),
                        ),
                        (conditionId) =>
                          `recipe-subtotal-density-${component.id}-${conditionId}`,
                      )}
                    </Fragment>
                  ))}
                  {ingredients.length > 0 ? (
                    <>
                      {recipeComponents.length > 0 ? (
                        <tr className="component-head">
                          <td className="td-label" colSpan={metricColumns}>
                            Estimert
                          </td>
                          <td />
                        </tr>
                      ) : null}
                      {ingredients.map((ing, index) => (
                        <tr
                          // biome-ignore lint/suspicious/noArrayIndexKey: rows are replaced wholesale per estimate or removed in place, never reordered.
                          key={index}
                          data-testid={`meal-ingredient-row-${index}`}
                        >
                          <td className="td-label">{ing.name}</td>
                          <td>
                            <input
                              type="number"
                              min={0}
                              step="any"
                              className="ingredient-grams"
                              value={gramsInputs[index] ?? ""}
                              onChange={(e) =>
                                changeGrams(index, e.target.value)
                              }
                              onFocus={() => focusGrams(index)}
                              onBlur={() => blurGrams(index)}
                              aria-label={`Gram ${ing.name}`}
                              data-testid={`ingredient-grams-input-${index}`}
                            />
                          </td>
                          {MEAL_METRICS.map((metric) => (
                            <td key={metric}>
                              {formatAmount(ing[metric])}
                              <MetricCellDensity
                                conditions={conditions}
                                metric={metric}
                                amounts={ing}
                                testidFor={(conditionId) =>
                                  `ingredient-density-${index}-${conditionId}`
                                }
                              />
                            </td>
                          ))}
                          <td className="td-actions">
                            <button
                              type="button"
                              className="icon-btn-delete"
                              onClick={() => removeIngredient(index)}
                              aria-label={`Fjern ${ing.name}`}
                              data-testid={`delete-ingredient-button-${index}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {subtotalRow(
                        "chat-subtotal-row",
                        <>
                          <span className="portion-times">×</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            className="portion-input"
                            value={chatScaleInput}
                            onChange={(e) => changeChatScale(e.target.value)}
                            aria-label="Spist av estimert del"
                            data-testid="chat-portion-input"
                          />
                          <span className="portion-label">Spist</span>
                        </>,
                        formatAmount(chatGrams * chatScale),
                        scaleMetrics(chatSubtotal, chatScale),
                        (conditionId) => `chat-subtotal-density-${conditionId}`,
                      )}
                    </>
                  ) : null}
                  <tr
                    className="row-total"
                    data-testid="meal-ingredients-total"
                  >
                    <td className="td-label">Totalt</td>
                    <td data-testid="meal-ingredients-total-grams">
                      {gramsCell(totalRawGrams, totalEatenGrams)}
                    </td>
                    {MEAL_METRICS.map((metric) => (
                      <td
                        key={metric}
                        data-testid={`meal-ingredients-total-${metric}`}
                      >
                        {formatAmount(totals[metric])}
                        <MetricCellDensity
                          conditions={conditions}
                          metric={metric}
                          amounts={totals}
                          testidFor={(conditionId) =>
                            `ingredient-density-total-${conditionId}`
                          }
                        />
                      </td>
                    ))}
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <p className="hint">
              Beskriv måltidet i meldingsfeltet under, så estimerer KI
              ingrediensene og næringsinnholdet.
            </p>
          )}

          <div className="meal-chat">
            {transcript.length > 0 ? (
              <div
                className="meal-chat-log"
                ref={chatLogRef}
                data-testid="meal-chat-log"
              >
                {transcript.map((message, index) => (
                  <p
                    // biome-ignore lint/suspicious/noArrayIndexKey: transcript is append-only.
                    key={index}
                    className={`chat-msg ${message.role}${message.event ? " event" : ""}`}
                  >
                    {message.text}
                  </p>
                ))}
              </div>
            ) : null}
            {chatError ? (
              <p className="field-error" data-testid="estimate-error">
                {chatError}
              </p>
            ) : null}
            <div className="meal-chat-compose">
              <textarea
                className="meal-chat-input"
                rows={10}
                placeholder={
                  transcript.length === 0
                    ? "f.eks. to brune skiver med gulost og et glass melk"
                    : "skriv en justering…"
                }
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault();
                    void send();
                  }
                }}
                data-testid="meal-chat-input"
              />
              <button
                type="button"
                className="btn"
                disabled={draft.trim().length === 0 || sending}
                onClick={() => void send()}
                data-testid="meal-chat-send-button"
              >
                {sending ? "Estimerer…" : "Send"}
              </button>
            </div>
            <p className="hint chat-send-hint" data-testid="chat-send-hint">
              Ctrl + Enter for å sende
            </p>
          </div>
        </>
      ) : (
        MEAL_METRICS.map((metric) => (
          <div className="setting-row" key={metric}>
            <label className="setting-label" htmlFor={`meal-${metric}`}>
              {METRIC_LABELS[metric]}
            </label>
            <div className="setting-control">
              <div className="num-input">
                <input
                  id={`meal-${metric}`}
                  type="number"
                  min={0}
                  step="any"
                  value={metricInputs[metric]}
                  onChange={(e) =>
                    setMetricInputs((prev) => ({
                      ...prev,
                      [metric]: e.target.value,
                    }))
                  }
                  data-testid={`meal-${metric}-input`}
                />
                <span className="unit">{metricUnit(metric)}</span>
              </div>
            </div>
          </div>
        ))
      )}

      <div className="modal-actions meal-form-actions">
        {mode === "chat" ? (
          <>
            <button
              type="button"
              className="btn ghost btn-left"
              onClick={handleManualClick}
              data-testid="manual-mode-button"
            >
              {hasComponents ? "Rediger manuelt" : "Legg til manuelt"}
            </button>
            <button
              type="button"
              className="btn ghost btn-left icon-btn"
              disabled={ingredients.length === 0}
              onClick={() => setSaveRecipeOpen(true)}
              data-testid="save-as-recipe-button"
            >
              <BookmarkPlus size={14} />
              Lagre som oppskrift
            </button>
          </>
        ) : null}
        <button
          type="button"
          className="btn ghost"
          onClick={onClose}
          data-testid="meal-cancel-button"
        >
          Avbryt
        </button>
        <button
          type="button"
          className="btn"
          disabled={!canSave}
          onClick={handleSave}
          data-testid="save-meal-button"
        >
          Lagre
        </button>
      </div>

      <RecipeEditorModal
        open={saveRecipeOpen}
        initial={null}
        prefill={{ name: name.trim(), ingredients }}
        recipes={recipes}
        foodClarifications={foodClarifications}
        onSave={saveRecipeFromEditor}
        onClose={() => setSaveRecipeOpen(false)}
      />

      <ConfirmModal
        open={confirmConvert}
        title="Rediger manuelt?"
        body="Ingredienslisten, oppskriftene og samtalen fjernes. Totalene beholdes."
        confirmLabel="Rediger manuelt"
        onConfirm={doConvert}
        onCancel={() => setConfirmConvert(false)}
        data-testid="convert-manual-modal"
      />
    </Modal>
  );
}
